import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { setLocalStorage, getLocalStorage } from 'utils/localStorageUtils';
import _, { has, isEmpty , forEach, compact } from "lodash";
import { getDataIndex } from "utils/tableUtils";

const storedData = getLocalStorage('userPreferences');
const initialState = {
  tablePreferences: storedData?.tablePreferences || {}
}

const UserPreferencesContext = createContext({
  ...initialState,
  updateTablePreferences: () => { },
  setInitialTablePreferences: () => { },
  updateColumnPreference: () => { },
  getSavedColumnPreferences: (tableKey, columns) => columns,
  getColumnOrder: (tableKey, columns) => columns,
  getTablePreferences: () => {},
  getActiveOptionalColumns: () => [],
});


const Actions = {
  UPDATE_TABLE_PREFERENCES: "update_table_preferences"
}

function reducer(state, action) {
  switch (action.type) {
    case Actions.UPDATE_TABLE_PREFERENCES: {

      const _state = {
        ...state,
        tablePreferences: {
          ...state.tablePreferences,
          [action.payload.tableKey]: {
            ...state.tablePreferences[action.payload.tableKey],
            [action.payload.preferenceKey]: action.payload.data
          }
        }
      }

      setLocalStorage('userPreferences', _state);
      return _state;
    }
    default:
      return state;
  }
}

export const UserPreferencesContextProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const updateTablePreferences = (payload) => {
    dispatch({
      type: Actions.UPDATE_TABLE_PREFERENCES,
      payload: payload
    })
  }

  function getSavedColumnPreferences(tableKey, columns) {
    const { tablePreferences } = state;
    if (!tableKey) {
      return columns;
    }
    let _columns = [];

    // check if user preference is stored else use columns recieved in params as is
    if (tablePreferences[tableKey] && !isEmpty(tablePreferences[tableKey].columnSequence)) {
      const { columnSequence: savedColumnsData } = tablePreferences[tableKey];
      _columns = columns.map((column) => {
        return {
          ...column,
          optional: !column.alwaysDisplay && (!isEmpty(column.title) && column.key !== 'action'),
          default: savedColumnsData[getDataIndex(column)] > -1,
        }
      });
    } else {
      _columns = columns.map((column) => ({
        ...column,
        optional: !column.alwaysDisplay && (!isEmpty(column.title) && column.key !== 'action'),
        default: has(column, 'default') ? column.default : true,
      }));
    }
    return _columns;
  }

  function getColumnOrder(tableKey, columns, keyToCheck = 'value') {
    const { tablePreferences } = state;
    if (!tableKey) {
      return columns;
    }
    
    if (tablePreferences[tableKey] && !isEmpty(tablePreferences[tableKey].columnSequence)) {
      const storedColumnsSequence = tablePreferences[tableKey].columnSequence;

      const result = []; // final array to return to container
      const orderNotFound = []; // columns whose order preference has not been found
      columns.forEach((column, _index) => {
        const dataIndex = keyToCheck === 'key' ? getDataIndex(column) : column[keyToCheck];
        const index = storedColumnsSequence[dataIndex];
        if (index > -1 && isEmpty(result[index])) {
          result[index] = column;
        } else {
          orderNotFound.push(column);
        }
      })
      // Push order not found columns to into the final result array
      forEach(orderNotFound, (value, key) => {
        const dataIndex = keyToCheck === 'key' ? getDataIndex(value) : value[keyToCheck];
        if (dataIndex === 'action') { // action column should always be at the end
          result.push(value);
        } else if (!isEmpty(result[key])) {
          result.splice(+(key), 0, value);
        } else {
          result[+(key)] = value;
        }
      });
      return compact(result);
    } else {
      return columns;
    }
  }

  function filterActiveColumns(columnSequence) {
    const activeColumns = [];
    _.forEach(columnSequence, (sequence, dataIndex) => {
      if (sequence > -1) {
        activeColumns[sequence] = dataIndex
      }
    });
    return _.compact(activeColumns);
  }

  function setInitialTablePreferences(tableKey, columns) {
    const { tablePreferences } = state;
    const columnSequence = {};
    const tableData = tablePreferences[tableKey] || {};
    const newColumnsAdded = [];
    columns.forEach((col) => {
      if (!col.optional) {
        return;
      }
      const key = getDataIndex(col);
      if (isEmpty(tableData.columnSequence) && !isEmpty(tableData.columnOrder)) {
        columnSequence[key] = tableData.columnOrder.indexOf(key);
      } else if (!has(tableData.columnSequence, key)) {
        if (col.default) {
          const sequneceArr = _.values(columnSequence);
          columnSequence[key] = (_.isEmpty(sequneceArr) ? -1 : Math.max(...sequneceArr)) + 1;
        } else {
          columnSequence[key] = -1;
        }
      } else {
        columnSequence[key] = tableData.columnSequence[key];
      }
      if (!has(tableData.columnSequence, key)) {
        newColumnsAdded.push(key);
      }
    });
    refreshSequence(tableKey, columnSequence);
    return newColumnsAdded.map(getDataIndex);
  }

  const updateColumnPreference = (checked, dataIndex, tableKey) => {
    const { tablePreferences } = state;
    if (!tablePreferences[tableKey]) {
      return;
    }
    const columnSequence = tablePreferences[tableKey].columnSequence;
    const sequnceValueArr = _.values(columnSequence); 
    const lastIndex = _.isEmpty(sequnceValueArr) ? -1 : Math.max(...sequnceValueArr);
    columnSequence[dataIndex] = checked ? (lastIndex + 1) : -1;
    
    // Refresh the sequence
    refreshSequence(tableKey, columnSequence);
  }

  const changeColumnSequence = (tableKey, source, destination) => {
    const { tablePreferences } = state;
    if (!tablePreferences[tableKey] || !destination || destination.index === source.index) {
      return;
    }
    let columnSequence = tablePreferences[tableKey].columnSequence;

    // rearrange the sequnce
    const newSequence = {};
    if (source.index < destination.index) {
      for (let i = source.index; i < destination.index; i++) {
        _.find(columnSequence, (sequenceValue, dataIndex) => {
          if (sequenceValue === (i + 1)) {
            newSequence[dataIndex] = i;
            return true;
          }
        });
      }
    } else {
      for (let i = destination.index; i < source.index; i++) {
        _.find(columnSequence, (sequenceValue, dataIndex) => {
          if (sequenceValue === i) {
            newSequence[dataIndex] = i + 1;
            return true;
          }
        });
      }
    }
    // Assign the draaging item with final index
    _.find(columnSequence, (sequenceValue, dataIndex) => {
      if (source.index === sequenceValue) {
        // dropped item
        newSequence[dataIndex] = destination.index;
        return true;
      }
    });
    
    columnSequence = {
      ...columnSequence,
      ...newSequence,
    };
    updateTablePreferences({ tableKey: tableKey, preferenceKey: 'columnSequence', data: columnSequence });
  }

  const refreshSequence = (tableKey, columnSequence) => {
    if (!tableKey || _.isEmpty(columnSequence)) {
      return;
    }
    const activeColumns = filterActiveColumns(columnSequence);
    _.forEach(activeColumns, (dataIndex, i) => {
      if (_.has(columnSequence, dataIndex)) {
        columnSequence[dataIndex] = i;
      }
    });
    updateTablePreferences({ tableKey: tableKey, preferenceKey: 'columnSequence', data: columnSequence });
  };

  const deleteTablePreference = (tableKey, preferenceKey = 'columnSequence') => {
    const tableData = getTablePreferences(tableKey);
    if (tableData[preferenceKey]) {
      delete tableData[preferenceKey];
    }
  };

  const getTablePreferences = (tableKey) => {
    return state.tablePreferences[tableKey] || {};
  }

  const getActiveOptionalColumns = useCallback((tableKey) => {
    const { tablePreferences } = state;
    const tableData = tablePreferences[tableKey];
    if (!tableData || _.isEmpty(tableData.columnSequence)) {
      return [];
    }
    const _activeOptionalColumns = filterActiveColumns(tableData.columnSequence);
    return _activeOptionalColumns;
  }, [state]);

  return (
    <UserPreferencesContext.Provider value={{ ...state, getActiveOptionalColumns, updateTablePreferences, getSavedColumnPreferences, getColumnOrder, changeColumnSequence, setInitialTablePreferences, updateColumnPreference, getTablePreferences, deleteTablePreference }}>
      {children}
    </UserPreferencesContext.Provider>
  )
}

export const withUserPreferencesContext = Component => (
  props => (
    <UserPreferencesContext.Consumer>
      {context => <Component userPreferencesContext={context} {...props} />}
    </UserPreferencesContext.Consumer>
  )
)

export function useUserPreferencesContext() {
  return useContext(UserPreferencesContext);
}
